/**
 * Browser download helpers (DOM; `downloadFile` uses Blob + object URLs).
 * Requires `document` (browser or happy-dom). `downloadFile` needs `Blob` and `URL.createObjectURL`.
 * `downloadFileFromUrl` uses `fetch` and `window.URL` / `window.webkitURL`.
 */

/**
 * Trigger a file download in the browser from string content (Blob + temporary object URL).
 *
 * @param {string} content
 * @param {string} [filename='download']
 * @param {string} [mimeType='application/octet-stream']
 */
export const downloadFile = (
    content,
    filename = 'download',
    mimeType = 'application/octet-stream',
) => {
    if (!content || typeof content !== 'string') {
        return;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * @param {string} imageBase64
 * @param {string} filename
 */
export const downloadImage = (imageBase64, filename) => {
    const a = document.createElement('a');
    a.href = imageBase64;
    a.download = filename;
    a.click();
};

/**
 * Fetches a URL, turns the response into a blob, then triggers a file download in the browser.
 *
 * @param {string} url
 * @param {string} [text] suggested file name (`download` if omitted or empty)
 */
export const downloadFileFromUrl = (url, text) => {
    if (!url) return;
    fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
            const urlObj = window.URL || window.webkitURL;
            const url = urlObj.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = text || 'download';
            document.body.appendChild(a);
            a.click();
            a.remove();
        })
        .catch((error) => console.error(error));
};

/**
 * Triggers a download using an existing URL (no `fetch`).
 *
 * @param {string} url
 * @param {string} [text] suggested file name (`download` if omitted or empty)
 */
export const openFileFromURl = (url, text) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = text || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
};
